// "Is the app behind the loading screen fully ready?" — so the overlay only lifts once
// the first screen can appear instantly and complete, not just once the scripts ran.
// (Zone Finder's separate ~34MB file stays on demand — deliberately not waited for.)

import { whenModulesReady } from "./modules/module-config.js";

// Fonts come from Google Fonts: if that's slow or blocked, the app still works with
// fallback fonts, so we stop waiting after this rather than hang on the loading screen.
const FONT_WAIT_MS = 3000;

const nextFrame = () => new Promise<void>((r) => requestAnimationFrame(() => r()));

/** index.html loads its stylesheets without blocking the first paint, marking each
 *  with data-loaded / data-failed. This waits for one (whether it has already settled
 *  or not). */
function sheetLoaded(link: HTMLLinkElement): Promise<void> {
  if (link.dataset.loaded) return Promise.resolve();
  const failed = () => new Error(`Stylesheet failed to load: ${link.href}`);
  if (link.dataset.failed) return Promise.reject(failed());
  return new Promise((resolve, reject) => {
    link.addEventListener("load", () => resolve(), { once: true });
    link.addEventListener("error", () => reject(failed()), { once: true });
  });
}

const stylesheets = () => [...document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')];
const isAppCss = (link: HTMLLinkElement) => new URL(link.href).origin === location.origin;
/** A sheet index.html loads without blocking the first paint: only those carry the
 *  data-loaded / data-failed markers. A sheet Vite adds at build time (e.g. flag-icons, #280)
 *  blocks rendering, so it's already loaded when this runs — and its "load" event has already
 *  fired, so waiting for it hung the loading screen on "Connecting…" (2026-10-16). */
const isDeferred = (link: HTMLLinkElement) => link.hasAttribute("onload");

/** The app's own CSS (styles.css). The legacy code measures the layout when it starts,
 *  so it mustn't run before this — and without it the app is unusable, so a failure
 *  throws (the loading screen then says so). */
export function stylesReady(): Promise<void> {
  const own = stylesheets().filter((l) => isAppCss(l) && isDeferred(l));
  if (!own.length) throw new Error("index.html has no styles.css <link>");
  return Promise.all(own.map(sheetLoaded)).then(() => undefined);
}


/** Every font weight/style the CSS declares — the browser otherwise only downloads a
 *  weight the first time something uses it, so text would change font after the fade.
 *  "Aa" picks the latin subset only (not every unicode-range file). */
async function fontsReady(): Promise<void> {
  const all = loadAllFonts().then((failures) => {
    for (const reason of failures) console.warn("A web font failed to load:", reason);
  });
  const timedOut = new Promise<"timeout">((r) => setTimeout(() => r("timeout"), FONT_WAIT_MS));
  if ((await Promise.race([all, timedOut])) === "timeout") {
    console.warn(`Web fonts still loading after ${FONT_WAIT_MS}ms — showing the app anyway.`);
  }
}

/** The Google Fonts stylesheets first (their @font-face rules only exist once they've
 *  loaded), then every face they declare. Resolves with any failures. */
async function loadAllFonts(): Promise<unknown[]> {
  const sheets = await Promise.allSettled(stylesheets().filter((l) => !isAppCss(l)).map(sheetLoaded));
  const faces = new Set<string>();
  for (const f of document.fonts) faces.add(`${f.style} ${f.weight} 16px "${f.family.replace(/"/g, "")}"`);
  const loads = await Promise.allSettled([...faces].map((font) => document.fonts.load(font, "Aa")));
  return [...sheets, ...loads].flatMap((r) => (r.status === "rejected" ? [r.reason] : []));
}

/** Resolves once the palette is populated, fonts are in, and the first screen (incl.
 *  any React module roots, which render asynchronously) has been drawn. */
export async function appReady(): Promise<void> {
  // modules.json + the side-panel tiles: its fetch falls back itself on failure, so this
  // always settles.
  await Promise.all([whenModulesReady(), fontsReady()]);
  // Two frames: React commits the module roots, then the browser paints them.
  await nextFrame();
  await nextFrame();
}
